'use client'

import { createContext, useContext, type ReactNode } from 'react'

export interface FeatureFlags {
  /** BOOKING_ENABLED on the serving host: the /book wizard and every Book Now CTA. */
  bookingEnabled: boolean
  /** ECOMMERCE_ENABLED on the serving host: products, cart and checkout. */
  ecommerceEnabled: boolean
  /** INSTANT_QUOTE_ENABLED on the serving host: /get-a-quote and every link to it. */
  instantQuoteEnabled: boolean
}

/** Fail closed: a component rendered outside the provider shows no booking, shop or instant-estimate CTA. */
const DEFAULT_FLAGS: FeatureFlags = {
  bookingEnabled: false,
  ecommerceEnabled: false,
  instantQuoteEnabled: false,
}

const FeatureFlagsContext = createContext<FeatureFlags>(DEFAULT_FLAGS)

/**
 * Carries the server-only feature flags into client components. The root
 * layout reads the env at request time and mounts this once, so a Book Now
 * button anywhere in the tree asks useFeatureFlags() instead of threading a
 * prop through every layer (which is how BottomNav ended up accepting an
 * ecommerceEnabled prop it never read).
 */
export function FeatureFlagsProvider({ value, children }: { value: FeatureFlags; children: ReactNode }) {
  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
}

export function useFeatureFlags(): FeatureFlags {
  return useContext(FeatureFlagsContext)
}
